import { assets } from "../assets/assets";
import Headers from "../components/Headers";
import TopBar from "../components/TopBar";

export default function AboutPage() {
  return (
    <div className="min-h-screen">

      <TopBar/>

      <div className="flex items-center justify-center w-full py-10">
        <h1 className="text-4xl font-black bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
          About
        </h1>
      </div>

      <div className="flex flex-col md:flex-row justify-center items-center px-6 md:px-36 lg:px-96 gap-10">
        <div>
          <img src={assets.profilePic} alt="Profile" className="rounded-full w-48 md:w-64 mx-auto ring-2 ring-pink-500/40 shadow-[0_0_30px_rgba(255,45,120,0.25)]"/>
          <div className="px-4 mt-6 flex justify-center">
            <a href="/WalkerMoodyResume.pdf" download>
              <button className="bg-gradient-to-r from-pink-500 to-violet-500 text-white font-bold px-6 py-2 rounded-full shadow-[0_0_16px_rgba(255,45,120,0.35)] hover:scale-105 transition-all duration-200">
                Download Resume
              </button>
            </a>
          </div>
        </div>

        <div className="max-w-2xl w-full space-y-6 text-left">
          <p className="text-gray-300 leading-relaxed">
            I'm a senior Computer Science student at{" "}
            <a href="https://www.ttu.edu"
               className="cursor-pointer font-bold text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
               target="_blank"
               rel="noopener noreferrer">
              Texas Tech University
            </a>
            {" "}with a 3.8 GPA, graduating in 2026. I build things end-to-end — from backend APIs and cloud infrastructure to frontends and desktop apps.
          </p>

          <p className="text-gray-300 leading-relaxed">
            I've shipped projects across a range of domains — from{" "}
            <a href="https://floriqai.com"
               className="cursor-pointer font-bold text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
               target="_blank"
               rel="noopener noreferrer">
              FloriqAI
            </a>
            , an AI-powered web app, to{" "}
            <a href="https://carelesssports.wamoody.com"
               className="cursor-pointer font-bold text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
               target="_blank"
               rel="noopener noreferrer">
              Careless Sports
            </a>
            , a full-stack sports content site. I've also built games in C++ with Raylib and Python with Pygame, a document redaction tool, and co-authored published research on lung cancer cell nucleus segmentation (ROBD).
          </p>

          <p className="text-gray-300 leading-relaxed">
            I intern at{" "}
            <a href="https://www.sailpoint.com/"
               className="cursor-pointer font-bold text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
               target="_blank"
               rel="noopener noreferrer">
              SailPoint
            </a>
            {" "}on the CIEM (Cloud Infrastructure Entitlement Management) team, where I built a Go connector between Oracle Cloud and AWS that lets customers manage cloud entitlements through SailPoint's SaaS platform.
          </p>

          <p className="text-gray-300 leading-relaxed">
            My core interests are cybersecurity, software engineering, and AI. I'm always building — whether that's a startup idea, a tool to automate something tedious, or a new system to learn from the ground up.
          </p>
        </div>
      </div>

      <Headers word1='Skills'/>
      <div className="flex flex-col justify-center px-6 md:px-36 lg:px-96 mt-6 gap-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Languages</p>
          <div className="flex flex-wrap gap-2">
            {['C', 'C++', 'Python', 'Go', 'HTML', 'React', 'Node.js', 'Assembly', 'JavaScript', 'TypeScript', 'SQL', 'Tailwind CSS'].map(skill => (
              <span key={skill} className="px-3 py-1 text-sm font-semibold rounded-full border border-pink-500/40 bg-pink-500/10 text-pink-300">
                {skill}
              </span>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Tools &amp; Platforms</p>
          <div className="flex flex-wrap gap-2">
            {['AWS Lambda', 'Step Functions', 'Git / GitHub', 'MongoDB', 'Oracle', 'Cloudinary', 'Vercel', 'Raylib', 'Pygame'].map(tool => (
              <span key={tool} className="px-3 py-1 text-sm font-semibold rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300">
                {tool}
              </span>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Focus Areas</p>
          <div className="flex flex-wrap gap-2">
            {['Cybersecurity', 'Software Engineering', 'Full-Stack Dev', 'Cloud Computing', 'Algorithms', 'Multithreading'].map(area => (
              <span key={area} className="px-3 py-1 text-sm font-semibold rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>

      <Headers word1='Experience'/>
      <div className="flex flex-col justify-center lg:items-center px-6 md:px-36 lg:px-96 mt-3 gap-6">
        <div className="w-full bg-[#1a1a1a] border border-gray-700/60 rounded-2xl p-6 shadow-[0_0_20px_rgba(255,45,120,0.06)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
            <p className="font-bold text-lg text-white">SailPoint — Backend Software Engineer</p>
            <span className="text-gray-500 text-sm">Summer 2024 · Returning Summer 2026</span>
          </div>
          <ul className="list-disc pl-5 space-y-1 text-gray-300 text-sm">
            <li>Interned for the Cloud Infrastructure Entitlement Management (CIEM) team</li>
            <li>Wrote a Go connector bridging Oracle Cloud and AWS for SailPoint customers</li>
            <li>Researched Oracle APIs to understand Oracle Cloud infrastructure</li>
            <li>Implemented the connector into AWS Step Functions and Lambda</li>
          </ul>
        </div>
        <div className="w-full bg-[#1a1a1a] border border-gray-700/60 rounded-2xl p-6 shadow-[0_0_20px_rgba(56,189,248,0.06)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
            <p className="font-bold text-lg text-white">Solis — Cybersecurity Intern</p>
            <span className="text-gray-500 text-sm">May 2023 – August 2023</span>
          </div>
          <ul className="list-disc pl-5 space-y-1 text-gray-300 text-sm">
            <li>Analyzed cybersecurity tooling: SentinelOne, Huntress, CyberCNS, DarkWebID, KnowBe4</li>
            <li>Wrote automation programs in C++ and Python to eliminate manual tasks</li>
            <li>Built a spreadsheet-to-formatted-PDF pipeline for customer reporting</li>
            <li>Manipulated and extracted data from client spreadsheets</li>
          </ul>
        </div>
      </div>

      <Headers word1='Academics'/>
      <div className="flex justify-center px-6 md:px-36 lg:px-96 mt-3">
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-2xl p-5">
            <p className="font-bold text-white">Texas Tech University</p>
            <p className="text-pink-400 font-semibold text-sm">GPA: 3.8</p>
            <p className="text-gray-400 text-sm mt-1">Computer Science · 2023–Present</p>
            <p className="text-gray-500 text-xs mt-1">Expected Graduation: 2026</p>
          </div>
          <div className="bg-[#1a1a1a] border border-gray-700/60 rounded-2xl p-5">
            <p className="font-bold text-white">Texas State University</p>
            <p className="text-cyan-400 font-semibold text-sm">GPA: 3.7</p>
            <p className="text-gray-400 text-sm mt-1">Computer Science · 2022–2023</p>
          </div>
        </div>
      </div>

      <Headers word1='Awards'/>
      <div className="flex flex-col justify-center lg:items-center px-6 md:px-36 lg:px-96 mt-3 mb-16">
        <div className="w-full bg-[#1a1a1a] border border-gray-700/60 rounded-2xl p-6">
          <ul className="list-disc pl-5 space-y-2 text-gray-300">
            <li>President's List — Texas Tech University, Spring 2025</li>
            <li>Dean's List — Texas Tech: Spring 2024, Fall 2023</li>
            <li>Dean's List — Texas State: Spring 2023, Fall 2022</li>
            <li>Texas Tech University Impact Scholarship</li>
            <li>Texas State Presidential Scholarship</li>
          </ul>
        </div>
      </div>

    </div>
  );
}
